export const escapeFontFamily = (family: string): string => {
  const escaped = CSS.escape(family);
  return /\s/.test(family) ? `'${escaped}'` : escaped;
};

// Mirrors the design-system default for --n-font-family-base. Tenant fonts load
// with font-display: swap, so without it shoppers would see the browser default
// font while the tenant font downloads, and for good if the download fails.
export const designSystemFontFallback = [
  'Figtree',
  'ui-sans-serif',
  'system-ui',
  'sans-serif',
];

const genericFontFamilies = new Set([
  'serif',
  'sans-serif',
  'monospace',
  'cursive',
  'fantasy',
  'system-ui',
  'ui-serif',
  'ui-sans-serif',
  'ui-monospace',
  'ui-rounded',
  'math',
  'emoji',
  'fangsong',
]);

// No escaping makes these safe in a family list: unquoted, a CSS-wide keyword
// invalidates the whole declaration and `default` is reserved, and quoted they
// name no real font.
const unusableFontFamilies = new Set([
  'inherit',
  'initial',
  'unset',
  'revert',
  'revert-layer',
  'default',
]);

// The list is merchant free text from Command, stored unbounded, and is written
// into the stylesheet once per served font. No real fallback list comes near
// either limit, so they only bound what a runaway value can cost the page.
const maxFallbackLength = 1000;
const maxFallbackFamilies = 20;

const unquote = (entry: string): string =>
  /^(["']).*\1$/s.test(entry) ? entry.slice(1, -1).trim() : entry;

// A comma inside a closed quote belongs to the name. A quote with no partner
// later in the list is plain text, so a stray one cannot swallow the entries
// after it.
const splitFontFamilyList = (list: string): string[] => {
  const entries: string[] = [];
  let entry = '';
  let quote: string | null = null;
  for (let i = 0; i < list.length; i++) {
    const char = list.charAt(i);
    if (quote) {
      if (char === quote) quote = null;
    } else if (char === ',') {
      entries.push(entry);
      entry = '';
      continue;
    } else if ((char === '"' || char === "'") && list.includes(char, i + 1)) {
      quote = char;
    }
    entry += char;
  }
  return [...entries, entry];
};

// The merchant's list is free text, so it is split into names here and each
// one escaped on the way out, never written into the stylesheet as entered.
const parseFontFamilies = (list: string | null): string[] =>
  splitFontFamilyList((list ?? '').slice(0, maxFallbackLength))
    .map((entry) => unquote(entry.trim()))
    .filter(
      (name) => name !== '' && !unusableFontFamilies.has(name.toLowerCase()),
    )
    .slice(0, maxFallbackFamilies);

const toFontFamilyCss = (name: string): string => {
  const keyword = name.toLowerCase();
  return genericFontFamilies.has(keyword) ? keyword : escapeFontFamily(name);
};

const fallbackFamilies = (
  merchantFallback: string | null,
  servedFamily?: string,
): string[] => {
  const seen = new Set<string>(
    servedFamily ? [servedFamily.toLowerCase()] : [],
  );
  return [...parseFontFamilies(merchantFallback), ...designSystemFontFallback]
    .filter((name) => {
      const key = name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map(toFontFamilyCss);
};

export const toFontStack = (family: string, merchantFallback: string | null) =>
  [
    escapeFontFamily(family),
    ...fallbackFamilies(merchantFallback, family),
  ].join(', ');

export const toFallbackFontStack = (merchantFallback: string | null) =>
  fallbackFamilies(merchantFallback).join(', ');
